import { FC } from "react";
import { EnterpriseAccountsStore } from "./enterpriseAccountsDomain";
import { observer } from "mobx-react";
import { useTranslation } from "@/stores/RootStoreContext";
import { Dialog } from "@/components-v2/Dialog";
import { Box, Card, VBox } from "@/components-v2/Box";
import { MCheckbox } from "@/components-v2/Checkbox";
import { MTextInput } from "@/components-v2/TextInput";
import { Button, LinkButton } from "@/components-v2/Button";
import { Tag } from "@/components-v2/Tag";
import { isOk } from "@/common/common";
import { capitalize, isNotBlank } from "@/utils/strings";

type SsoProvider = "google" | "microsoft";

const ssoProviders: SsoProvider[] = ["google", "microsoft"];

export const SsoSettings: FC<{ store: EnterpriseAccountsStore }> = observer(({ store }) => {
  const { t } = useTranslation();
  if (!isOk(store.accountResult)) {
    return null;
  }
  const account = store.accountResult.data.account;
  return (
    <VBox gap={"lg"}>
      {ssoProviders.map((provider) => {
        const domains = account.ssoSettings?.[provider]?.domains ?? [];
        const isActive = domains.some((d) => isNotBlank(d));
        const openDialog = () => {
          store.submitSSOSettingsResult = undefined;
          store.toggleSSODialog(provider);
        };
        return (
          <Card
            key={provider}
            padding
            gap={"lg"}
            maxWidth={"xl"}
            grow={"golden"}
            justifyContent={"center"}
          >
            <Box justifyContent={"space-between"} alignItems={"start"}>
              <Box gap={"sm"} alignItems={"center"}>
                <h4>
                  {provider === "google"
                    ? t("integrations.google-sso")
                    : t("integrations.microsoft-sso")}
                </h4>
                <Tag
                  color={isActive ? "success" : "neutral"}
                  label={isActive ? t("integrations.active") : t("inactive")}
                  compact
                />
              </Box>
              {isActive ? (
                <LinkButton
                  weight={"normal"}
                  size={"sm"}
                  icon={"pen"}
                  label={t("edit")}
                  onClick={openDialog}
                />
              ) : (
                <Button
                  variant={"secondary"}
                  size={"sm"}
                  label={t("sso.setup.title", { provider: capitalize(provider) })}
                  onClick={openDialog}
                  fullWidth={false}
                />
              )}
            </Box>
            <div>
              {provider === "google"
                ? t("integrations.sso.google-description")
                : t("integrations.sso.microsoft-description")}
            </div>
            <Box alignItems={"start"} columnGap={"lg"}>
              <div className={"min-w-40"}>{t("sso.setup.allowed-domains")}</div>
              <div>{domains.length > 0 ? domains.join(", ") : t("none")}</div>
            </Box>
          </Card>
        );
      })}
    </VBox>
  );
});

export const SsoSettingsDialog: FC<{ store: EnterpriseAccountsStore }> = observer(({ store }) => {
  const { t } = useTranslation();
  if (
    !(
      store.integrationSettingsModel?.type === "google" ||
      store.integrationSettingsModel?.type === "microsoft"
    )
  ) {
    return null;
  } else {
    const model = store.integrationSettingsModel;
    const isExistingProvider =
      isOk(store.accountResult) &&
      (store.accountResult.data.account.ssoSettings?.[model.type]?.domains?.length ?? 0) > 0;
    return (
      <Dialog
        title={{
          key: "sso.setup.title",
          params: { provider: model.type },
        }}
        maxWidth={"sm"}
        actions={
          <Box justifyContent={"space-between"} justifySelf={"end"} gap={"xl"}>
            {isExistingProvider && (
              <Box>
                <LinkButton
                  icon={"trash"}
                  label={t("sso.delete")}
                  color={"error"}
                  weight={"normal"}
                  onClick={() => store.deleteSSOSettings()}
                />
              </Box>
            )}
            <Box>
              <Button
                variant={"secondary"}
                label={t("cancel")}
                onClick={() => store.toggleSSODialog(model.type)}
              />
              <Button
                variant={"primary"}
                label={t("save")}
                onClick={() => {
                  if (model.validate()) {
                    store.updateSSOSettings();
                  }
                }}
              />
            </Box>
          </Box>
        }
        submitLabel={t("sharepoint.connect")}
        submitResult={store.submitSSOSettingsResult}
      >
        <VBox gap={"lg"}>
          {t({ key: "sso.setup.description", params: { provider: model.type } })}
          <div>{t("sso.domain")}</div>
          {model.domains.map((domain, index) => (
            <MTextInput key={index} model={domain} />
          ))}
          <Box>
            <LinkButton
              icon={"plus"}
              label={t("sso.add-domain")}
              onClick={() => model.addDomain()}
            />
          </Box>
          <VBox gap={"sm"}>
            <div>{t("sso.security")}</div>
            <MCheckbox
              model={model.requireSsoForAllUsers}
              label={{
                key: "sso.require-for-all-users",
                params: { ssoName: capitalize(model.type) },
              }}
            />
            <MCheckbox
              model={model.requireBankIdOnInitialSetup}
              label={{ key: "sso.require-bankid-on-initial-setup" }}
            />
          </VBox>
        </VBox>
      </Dialog>
    );
  }
});
